/**
 * WatchListTable — every flight-tracker watch (enabled and disabled), with the
 * disable/enable actions (CHO-769). Prop-driven: the page owns fetching and
 * passes watches + callbacks in.
 *
 * Disable is a two-step confirm, because the action is intentionally
 * one-way-by-design: the tracker CLI has no delete subcommand — observations
 * attribute to the immutable watch name — so disable is the only
 * end-of-life, and the copy says so up front.
 */
import { useState } from 'react';
import type { TrackerWatch } from '../../types/flights';

export const DISABLE_EXPLAINER =
  'Disabling stops future queries. All observed price history is kept and the watch can be re-enabled at any time. Watches cannot be deleted.';

export interface WatchListTableProps {
  watches: TrackerWatch[];
  /** Watch names with a mutation in flight (pending toggle, or being saved) —
   *  those rows show busy and their action buttons are disabled. */
  busyNames?: ReadonlySet<string>;
  /** Disable every Edit button — the page locks editor switching while a save
   *  is in flight, so a completing save can never close an editor opened for
   *  a different watch (CHO-769 review round 4). */
  editLocked?: boolean;
  onEdit: (watch: TrackerWatch) => void;
  /** Fires when the operator confirms the enable/disable action. */
  onToggleEnabled: (watch: TrackerWatch) => void;
}

export function WatchListTable({ watches, busyNames, editLocked = false, onEdit, onToggleEnabled }: WatchListTableProps) {
  // The watch whose Disable button has been pressed and now shows the confirm.
  const [confirmingDisable, setConfirmingDisable] = useState<string | null>(null);

  if (watches.length === 0) {
    return <p className="text-sm text-gray-500">No watches configured yet.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Route</th>
            <th className="py-2 pr-4">Outbound</th>
            <th className="py-2 pr-4">Inbound</th>
            <th className="py-2 pr-4">Carriers</th>
            <th className="py-2 pr-4">Trip types</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {watches.map((w) => {
            const muted = !w.enabled;
            const busy = busyNames?.has(w.name) ?? false;
            const rowClass = `border-b border-gray-100 last:border-0 ${muted ? 'text-gray-400' : ''}`;
            return (
              <tr key={w.name} className={rowClass}>
                <td className="py-2 pr-4 font-mono whitespace-nowrap">
                  {w.name}
                  {muted && (
                    <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs">
                      disabled
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4 font-mono whitespace-nowrap">
                  {w.origin} → {w.destinations.join(', ')}
                </td>
                <td className="py-2 pr-4 whitespace-nowrap">
                  {w.outbound.from} → {w.outbound.to}
                </td>
                <td className="py-2 pr-4 whitespace-nowrap">
                  {w.inbound.from} → {w.inbound.to}
                </td>
                <td className="py-2 pr-4 font-mono">{w.carriers.join(', ')}</td>
                <td className="py-2 pr-4">{w.trip_types.join(', ')}</td>
                <td className="py-2 pr-4">
                  <span
                    className={
                      w.enabled
                        ? 'inline-block px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs'
                        : 'inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs'
                    }
                  >
                    {w.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </td>
                <td className="py-2 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(w)}
                      disabled={busy || editLocked}
                      className="px-2 py-1 rounded border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Edit
                    </button>
                    {w.enabled ? (
                      confirmingDisable === w.name ? (
                        <span className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setConfirmingDisable(null);
                              onToggleEnabled(w);
                            }}
                            disabled={busy}
                            className="px-2 py-1 rounded bg-red-600 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                          >
                            {busy ? 'Disabling…' : 'Confirm disable'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmingDisable(null)}
                            className="px-2 py-1 rounded border border-gray-300 text-xs font-medium text-gray-600 hover:bg-gray-100"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmingDisable(w.name)}
                          disabled={busy}
                          className="px-2 py-1 rounded border border-red-300 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                        >
                          Disable
                        </button>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => onToggleEnabled(w)}
                        disabled={busy}
                        className="px-2 py-1 rounded border border-green-300 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
                      >
                        {busy ? 'Enabling…' : 'Enable'}
                      </button>
                    )}
                  </div>
                  {confirmingDisable === w.name && (
                    <p className="mt-1 max-w-xs text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
                      {DISABLE_EXPLAINER}
                    </p>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
